import PropTypes from "prop-types";
import React, { Fragment } from "react";
import {
  useAsyncDebounce,
  useExpanded,
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from "react-table";
import { Button, CardBody, Col, Input, Row, Table } from "reactstrap";
import {
  AppelOffressGlobalFilter,
  CategoriesGlobalFilter,
  CollaborateurGlobalFilter,
  CompaniesGlobalFilter,
  ContactsGlobalFilter,
  CreateUserCategorie,
  CreateUserSubcategorie,
  CreateUserLink,
  CryptoOrdersGlobalFilter,
  CustomersGlobalFilter,
  ExpertGlobalFilter,
  FormationsGlobalFilter,
  InnovationsGlobalFilter,
  InvoiceListGlobalSearch,
  LeadsGlobalFilter,
  MetiersGlobalFilter,
  NFTRankingGlobalFilter,
  OrderGlobalFilter,
  ProductsGlobalFilter,
  PublicitiesGlobalFilter,
  RecrutementsGlobalFilter,
  TaskListGlobalFilter,
  TicketsListGlobalFilter,
  TypesAbonnementGlobalFilter,
} from "../../Components/Common/GlobalSearchFilter";
import { DefaultColumnFilter } from "./filters";

// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
  isCustomerFilter,
  isOrderFilter,
  isContactsFilter,
  isCompaniesFilter,
  isCryptoOrdersFilter,
  isInvoiceListFilter,
  isTicketsListFilter,
  isNFTRankingFilter,
  isTaskListFilter,
  isProductsFilter,
  isRecrutementsFilter,
  isLeadsFilter,
  isAppelsOffresFilter,
  isCategoryFilter,
  isMetierFilter,
  isCollaborateurFilter,
  isExpertFilter,
  isFormationFilter,
  isInnovationFilter,
  isPublicitiesFilter,
  isAddUserList,
  isTypeAbonnementFilter,
  isAddUserCategorie,
  isAddUserSubcategorie,
  globalSearchWidth,
  onGlobalSearchChange,
  globalSearchDebounceMs,
  globalSearchValue,
}) {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = React.useState(globalFilter);

  React.useEffect(() => {
    if (globalSearchValue === undefined) return;
    setValue(globalSearchValue);
  }, [globalSearchValue]);

  const onChange = useAsyncDebounce((value) => {
    if (onGlobalSearchChange) {
      onGlobalSearchChange(value || "");
      setGlobalFilter(undefined);
      return;
    }
    setGlobalFilter(value || undefined);
  }, globalSearchDebounceMs || 200);

  return (
    <React.Fragment>
      <CardBody className="border border-dashed border-end-0 border-start-0">
        <form>
          <Row>
            <Col sm={5}>
              <div
                className={
                  isTypeAbonnementFilter ||
                  isInnovationFilter ||
                  isPublicitiesFilter ||
                  isFormationFilter ||
                  isExpertFilter ||
                  isCollaborateurFilter ||
                  isCategoryFilter ||
                  isRecrutementsFilter ||
                  isAppelsOffresFilter ||
                  isProductsFilter ||
                  isContactsFilter ||
                  isCompaniesFilter ||
                  isNFTRankingFilter
                    ? "search-box me-2 mb-2 d-inline-block"
                    : "search-box me-2 mb-2 d-inline-block col-12"
                }
                style={
                  globalSearchWidth
                    ? {
                        width: globalSearchWidth,
                        maxWidth: "100%",
                      }
                    : undefined
                }
              >
                <input
                  onChange={(e) => {
                    setValue(e.target.value);
                    onChange(e.target.value);
                  }}
                  id="search-bar-0"
                  type="text"
                  className="form-control search /"
                  placeholder={`Rechercher dans ${count} donnée(s)...`}
                  value={value || ""}
                />
                <i className="bx bx-search-alt search-icon"></i>
              </div>
            </Col>
            {isExpertFilter && <ExpertGlobalFilter />}
            {isTypeAbonnementFilter && <TypesAbonnementGlobalFilter />}
            {isPublicitiesFilter && <PublicitiesGlobalFilter />}
            {isFormationFilter && <FormationsGlobalFilter />}
            {isInnovationFilter && <InnovationsGlobalFilter />}
            {isProductsFilter && <ProductsGlobalFilter />}
            {isAddUserList && <CreateUserLink />}
            {isCategoryFilter && <CategoriesGlobalFilter />}
            {isCollaborateurFilter && <CollaborateurGlobalFilter />}
            {isAppelsOffresFilter && <AppelOffressGlobalFilter />}
            {isRecrutementsFilter && <RecrutementsGlobalFilter />}
            {isCustomerFilter && <CustomersGlobalFilter />}
            {isMetierFilter && <MetiersGlobalFilter />}
            {isOrderFilter && <OrderGlobalFilter />}
            {isContactsFilter && <ContactsGlobalFilter />}
            {isCompaniesFilter && <CompaniesGlobalFilter />}
            {isLeadsFilter && <LeadsGlobalFilter />}
            {isCryptoOrdersFilter && <CryptoOrdersGlobalFilter />}
            {isInvoiceListFilter && <InvoiceListGlobalSearch />}
            {isTicketsListFilter && <TicketsListGlobalFilter />}
            {isNFTRankingFilter && <NFTRankingGlobalFilter />}
            {isTaskListFilter && <TaskListGlobalFilter />}
            {isAddUserCategorie && <CreateUserCategorie />}
            {isAddUserSubcategorie && <CreateUserSubcategorie />}
          </Row>
        </form>
      </CardBody>
    </React.Fragment>
  );
}

const TableContainer = ({
  columns,
  data,
  isRecrutementsFilter,
  isGlobalSearch,
  isGlobalFilter,
  isProductsFilter,
  isCustomerFilter,
  isOrderFilter,
  isContactsFilter,
  isCompaniesFilter,
  isLeadsFilter,
  isMetierFilter,
  isCategoryFilter,
  isCryptoOrdersFilter,
  isInvoiceListFilter,
  isTicketsListFilter,
  isNFTRankingFilter,
  isTaskListFilter,
  isAppelsOffresFilter,
  isCollaborateurFilter,
  isExpertFilter,
  isAddOptions,
  isAddUserList,
  isAddUserCategorie,
  isAddUserSubcategorie,
  handleOrderClicks,
  handleCustomerClick,
  isAddCustList,
  customPageSize,
  tableClass,
  theadClass,
  trClass,
  thClass,
  divClass,
  isFormationFilter,
  isInnovationFilter,
  isPublicitiesFilter,
  isTypeAbonnementFilter,
  globalSearchWidth,
  onGlobalSearchChange,
  globalSearchDebounceMs,
  globalSearchValue,
}) => {
  const globalFilterFn = React.useCallback((rows, columnIds, filterValue) => {
    if (filterValue === undefined || filterValue === null) return rows;
    const search = String(filterValue).toLowerCase().trim();
    if (!search) return rows;

    return rows.filter((row) => {
      try {
        const valuesText = columnIds
          .map((id) => {
            const v = row?.values?.[id];
            if (v === null || v === undefined) return "";
            if (typeof v === "string") return v;
            if (typeof v === "number" || typeof v === "boolean")
              return String(v);
            try {
              return JSON.stringify(v);
            } catch (_e) {
              return String(v);
            }
          })
          .join(" ")
          .toLowerCase();

        if (valuesText.includes(search)) return true;
      } catch (_e) {
        // ignore
      }

      try {
        return JSON.stringify(row.original).toLowerCase().includes(search);
      } catch (_e) {
        return false;
      }
    });
  }, []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: DefaultColumnFilter },
      globalFilter: globalFilterFn,
      initialState: {
        pageIndex: 0,
        pageSize: customPageSize,
        selectedRowIds: 0,
        sortBy: [
          {
            desc: false,
          },
        ],
      },
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );

  const generateSortingIndicator = (column) => {
    return column.isSorted ? (column.isSortedDesc ? " " : "") : "";
  };

  const onChangeInSelect = (event) => {
    setPageSize(Number(event.target.value));
  };
  const onChangeInInput = (event) => {
    const page = event.target.value ? Number(event.target.value) - 1 : 0;
    gotoPage(page);
  };

  return (
    <Fragment>
      <Row className="mb-3">
        {isGlobalSearch && (
          <Col md={1}>
            <select
              className="form-select"
              value={pageSize}
              onChange={onChangeInSelect}
            >
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </Col>
        )}
        {isGlobalFilter && (
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={state.globalFilter}
            setGlobalFilter={setGlobalFilter}
            isProductsFilter={isProductsFilter}
            isCategoryFilter={isCategoryFilter}
            isCollaborateurFilter={isCollaborateurFilter}
            isExpertFilter={isExpertFilter}
            isRecrutementsFilter={isRecrutementsFilter}
            isAppelsOffresFilter={isAppelsOffresFilter}
            isCustomerFilter={isCustomerFilter}
            isOrderFilter={isOrderFilter}
            isContactsFilter={isContactsFilter}
            isCompaniesFilter={isCompaniesFilter}
            isLeadsFilter={isLeadsFilter}
            isCryptoOrdersFilter={isCryptoOrdersFilter}
            isInvoiceListFilter={isInvoiceListFilter}
            isTicketsListFilter={isTicketsListFilter}
            isNFTRankingFilter={isNFTRankingFilter}
            isTaskListFilter={isTaskListFilter}
            isMetierFilter={isMetierFilter}
            isFormationFilter={isFormationFilter}
            isInnovationFilter={isInnovationFilter}
            isPublicitiesFilter={isPublicitiesFilter}
            isTypeAbonnementFilter={isTypeAbonnementFilter}
            isAddUserList={isAddUserList}
            isAddUserCategorie={isAddUserCategorie}
            isAddUserSubcategorie={isAddUserSubcategorie}
            globalSearchWidth={globalSearchWidth}
            onGlobalSearchChange={onGlobalSearchChange}
            globalSearchDebounceMs={globalSearchDebounceMs}
            globalSearchValue={globalSearchValue}
          />
        )}
        {isAddOptions && (
          <Col sm="7">
            <div className="text-sm-end">
              <Button
                type="button"
                color="success"
                className="btn-rounded  mb-2 me-2"
                onClick={handleOrderClicks}
              >
                <i className="mdi mdi-plus me-1" />
                Add New Order
              </Button>
            </div>
          </Col>
        )}
        {isAddCustList && (
          <Col sm="7">
            <div className="text-sm-end">
              <Button
                type="button"
                color="success"
                className="btn-rounded mb-2 me-2"
                onClick={handleCustomerClick}
              >
                <i className="mdi mdi-plus me-1" />
                New Customers
              </Button>
            </div>
          </Col>
        )}
      </Row>

      <div className={divClass}>
        <Table hover {...getTableProps()} className={tableClass}>
          <thead className={theadClass}>
            {headerGroups.map((headerGroup) => (
              <tr
                className={trClass}
                key={headerGroup.id}
                {...headerGroup.getHeaderGroupProps()}
              >
                {headerGroup.headers.map((column) => (
                  <th
                    key={column.id}
                    className={thClass}
                    {...column.getSortByToggleProps()}
                  >
                    {column.render("Header")}
                    {generateSortingIndicator(column)}
                    {/* <Filter column={column} /> */}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <Fragment key={row.id}>
                  <tr key={row.id} {...row.getRowProps({ key: undefined })}>
                    {row.cells.map((cell) => {
                      return (
                        <td key={cell.column.id} {...cell.getCellProps()}>
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </Table>
      </div>

      <Row className="justify-content-md-end justify-content-center align-items-center p-2">
        <Col className="col-md-auto">
          <div className="d-flex gap-1">
            <Button
              color="primary"
              onClick={previousPage}
              disabled={!canPreviousPage}
            >
              {"<"}
            </Button>
          </div>
        </Col>
        <Col className="col-md-auto d-none d-md-block">
          Page{" "}
          <strong>
            {pageIndex + 1} of {pageOptions.length}
          </strong>
        </Col>
        <Col className="col-md-auto">
          <Input
            type="number"
            min={1}
            style={{ width: 70 }}
            max={pageOptions.length}
            defaultValue={pageIndex + 1}
            onChange={onChangeInInput}
          />
        </Col>

        <Col className="col-md-auto">
          <div className="d-flex gap-1">
            <Button color="primary" onClick={nextPage} disabled={!canNextPage}>
              {">"}
            </Button>
          </div>
        </Col>
      </Row>
    </Fragment>
  );
};

TableContainer.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
  globalSearchWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onGlobalSearchChange: PropTypes.func,
  globalSearchDebounceMs: PropTypes.number,
  globalSearchValue: PropTypes.string,
};

export default TableContainer;
